import { useEffect, useRef } from 'react';
import HanziWriter from 'hanzi-writer';
import { motion as Motion, useMotionValue, useTransform, useAnimationControls } from 'framer-motion';
import { SPRING_SNAPPY } from '../../constants/motion';
import s from './ReviewCard.module.css';

const SWIPE_THRESHOLD = 110;
const SWIPE_VELOCITY = 600;
const WRITER_SIZE = 180;

export default function ReviewCard({ character, onSwipeRight, onSwipeLeft }) {
  const writerRef = useRef(null);
  const controls = useAnimationControls();
  const x = useMotionValue(0);

  const rotate = useTransform(x, [-220, 0, 220], [-14, 0, 14]);
  const greenOpacity = useTransform(x, [0, 140], [0, 0.5]);
  const orangeOpacity = useTransform(x, [-140, 0], [0.5, 0]);

  useEffect(() => {
    const node = writerRef.current;
    if (!character?.char || !node) return undefined;
    node.innerHTML = '';
    const writer = HanziWriter.create(node, character.char, {
      width: WRITER_SIZE,
      height: WRITER_SIZE,
      padding: 8,
      showOutline: true,
      strokeAnimationSpeed: 1,
      delayBetweenStrokes: 250,
    });

    // I-VIEWBOX-01 patch: HanziWriter doesn't set a viewBox; without it CSS
    // scaling breaks. Match the writer's native dimensions.
    const svg = node.querySelector('svg');
    if (svg) {
      svg.setAttribute('viewBox', `0 0 ${WRITER_SIZE} ${WRITER_SIZE}`);
      svg.removeAttribute('width');
      svg.removeAttribute('height');
    }

    // Play once on mount — do NOT loop.
    writer.animateCharacter();
    return () => {
      node.innerHTML = '';
    };
  }, [character?.char]);

  function handleDragEnd(_event, info) {
    const dx = info.offset.x;
    const vx = info.velocity.x;
    const commitRight = dx > SWIPE_THRESHOLD || vx > SWIPE_VELOCITY;
    const commitLeft = dx < -SWIPE_THRESHOLD || vx < -SWIPE_VELOCITY;

    if (commitRight) {
      controls.start({ x: 600, opacity: 0, transition: SPRING_SNAPPY });
      onSwipeRight?.(character.id);
      return;
    }
    if (commitLeft) {
      controls.start({ x: -600, opacity: 0, transition: SPRING_SNAPPY });
      onSwipeLeft?.(character.id);
      return;
    }
    controls.start({ x: 0, transition: SPRING_SNAPPY });
  }

  if (!character) return null;

  return (
    <Motion.div
      className={s.card}
      style={{ x, rotate }}
      drag="x"
      dragConstraints={{ left: 0, right: 0 }}
      dragElastic={0.6}
      onDragEnd={handleDragEnd}
      animate={controls}
      initial={{ opacity: 0, y: 24, scale: 0.96 }}
      whileInView={{ opacity: 1, y: 0, scale: 1 }}
      transition={SPRING_SNAPPY}
    >
      {/* Color washes — derived from drag position */}
      <Motion.div
        className={`${s.wash} ${s.washGreen}`}
        style={{ opacity: greenOpacity }}
        aria-hidden="true"
      />
      <Motion.div
        className={`${s.wash} ${s.washOrange}`}
        style={{ opacity: orangeOpacity }}
        aria-hidden="true"
      />

      {/* Stroke animation */}
      <div className={s.writerWrap}>
        <div ref={writerRef} className={s.writer} />
      </div>

      {/* Text */}
      <div className={s.textBlock}>
        {character.pinyin && (
          <p className={s.pinyin}>{character.pinyin}</p>
        )}
        {character.meaning && (
          <p className={s.meaning}>{character.meaning}</p>
        )}
      </div>
    </Motion.div>
  );
}
